import {Helmet} from '@dr.pogodin/react-helmet';
import {Action, closeCurrentDialog, openDialog} from 'platform/components';

import {useRef} from 'react';

import {not} from 'ramda';

import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {testIds, warehouseRoutes} from '@dms/routes';
import {
  DocumentExtractionImportDialog,
  Main,
  PageTab,
  refreshDmsDatagrid,
  usePermissions,
  WAREHOUSE_GRID_CODES,
} from '@dms/shared';
import {DeliveryNoteList} from '@dms/warehouse/delivery-note';

import {buildArray, pushGtmEvent, useNavigate} from 'shared/web';

import {
  ReceiveNoteCorrectionsHandle,
  ReceiveNoteCorrectionsList,
} from '../ReceiveNoteCorrectionsList/ReceiveNoteCorrectionsList';
import {DownloadNew} from './components/DownloadNew';
import {ReceiveNoteList, ReceiveNoteListHandle} from './components/ReceiveNoteList';

export function ReceiveNoteLists() {
  const navigate = useNavigate();

  const [
    canCreateReceiveNote,
    canReadReceiveNote,
    canReadDeliveryNote,
    canDownloadDeliveryNote,
    receiveNoteCorrectionRead,
  ] = usePermissions({
    permissionKeys: [
      'receiveNoteCreate',
      'receiveNoteRead',
      'deliveryNoteRead',
      'deliveryNoteDownload',
      'receiveNoteCorrectionRead',
    ],
  });

  const receiveNoteListHandleRef = useRef<ReceiveNoteListHandle>(null);
  const receiveNoteCorrectionsListHandleRef = useRef<ReceiveNoteCorrectionsHandle>(null);

  const handleExtractDeliveryNote = () => {
    pushGtmEvent({
      event: 'button_click',
      event_id: 'ai_tool_dn_import_dialog_open',
    });

    openDialog(
      <DocumentExtractionImportDialog
        dialogId={EXTRACT_DELIVERY_NOTE_DIALOG_ID}
        data-testid={testIds.warehouse.receiveNoteList(EXTRACT_DELIVERY_NOTE_DIALOG_ID)}
      />,
      {
        id: EXTRACT_DELIVERY_NOTE_DIALOG_ID,
      }
    );
  };

  const handleDownloadNew = () => {
    openDialog(
      <DownloadNew
        onDiscard={closeCurrentDialog}
        onSave={() => {
          closeCurrentDialog();
          refreshDmsDatagrid(WAREHOUSE_GRID_CODES.deliveryNoteList);
          receiveNoteListHandleRef.current?.refreshDataGrid();
          receiveNoteCorrectionsListHandleRef.current?.refreshDataGrid();
        }}
        data-testid={testIds.warehouse.receiveNoteList('downloadNewDialog')}
      />,
      {
        size: 'small',
        title: i18n.t('entity.warehouse.labels.downloadNew'),
      }
    );
  };

  const tabs = buildArray<PageTab>()
    .when(canReadDeliveryNote, () => ({
      id: 'deliveryNotes',
      title: i18n.t('page.warehouse.labels.deliveryNotes'),
      content: <DeliveryNoteList />,
      href: warehouseRoutes.deliveryNoteList,
    }))
    .when(canReadReceiveNote, () => ({
      id: 'receiveNotes',
      title: i18n.t('page.warehouse.labels.receiveNotes'),
      content: <ReceiveNoteList ref={receiveNoteListHandleRef} />,
      href: warehouseRoutes.receiveNoteList,
    }))
    .when(receiveNoteCorrectionRead, () => ({
      id: 'receiveCorrections',
      title: i18n.t('page.warehouse.labels.receiveCorrections'),
      content: <ReceiveNoteCorrectionsList ref={receiveNoteCorrectionsListHandleRef} />,
      href: warehouseRoutes.receiveNoteCorrectionsList,
    }));

  const actions = buildArray<Action>()
    .whenFeatureEnabled(featureFlags.AFS_WAREHOUSE_EXTRACT_DELIVERY_NOTE, {
      type: 'button',
      variant: 'secondary',
      title: i18n.t('entity.documentExtraction.actions.extractDeliveryNote'),
      onClick: handleExtractDeliveryNote,
      'data-testid': testIds.warehouse.receiveNoteList('headerActions.extractDeliveryNote'),
    })
    .add({
      isDisabled: not(canDownloadDeliveryNote),
      variant: 'secondary',
      tooltip: {
        label: i18n.t('general.labels.noPermission'),
        isDisabled: canDownloadDeliveryNote,
      },
      type: 'buttonWithTooltip',
      title: i18n.t('entity.warehouse.labels.downloadNew'),
      onClick: handleDownloadNew,
      'data-testid': testIds.warehouse.receiveNoteList('headerActions.downloadNew'),
    })
    .add({
      isDisabled: not(canCreateReceiveNote),
      type: 'buttonWithTooltip',
      title: i18n.t('entity.warehouse.labels.newReceiveNote'),
      onClick: () => navigate(warehouseRoutes.receiveNoteCreate),
      tooltip: {
        label: i18n.t('general.labels.noPermission'),
        isDisabled: canCreateReceiveNote,
      },
      'data-testid': testIds.warehouse.receiveNoteList('headerActions.new'),
    });

  return (
    <>
      <Helmet title={i18n.t('module.warehouse.title')} />
      <Main
        isFullHeight
        tabs={tabs}
        actions={actions}
        data-testid={testIds.warehouse.receiveNoteList('createReceiveNote')}
      />
    </>
  );
}

const EXTRACT_DELIVERY_NOTE_DIALOG_ID = 'extractDeliveryNoteDialog';
